<template>
  <auth-shell>
    <p class="auth-form__eyebrow">
      {{ isInvitation ? 'Bienvenido' : 'Tu cuenta' }}
    </p>
    <h1 class="auth-form__title auth-form__title--with-lead">
      {{ isInvitation ? 'Crea tu contraseña' : $t('resetPassword') }}
    </h1>
    <p class="auth-form__lead">
      {{ isInvitation ? 'Elige la contraseña con la que vas a entrar a YoConstruyo.' : 'Escribe tu contraseña nueva dos veces para confirmarla.' }}
    </p>

    <validation-observer
      ref="simpleRules"
      #default="{ invalid }"
    >
      <b-form
        class="auth-form__fields"
        method="POST"
        @submit.prevent="validationForm"
      >
        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Contraseña"
          vid="Password"
          rules="required|min:8"
        >
          <label
            class="sr-only"
            for="reset-password-new"
          >Contraseña nueva</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="LockIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="reset-password-new"
              v-model="password"
              :type="password1FieldType"
              autocomplete="new-password"
              placeholder="Contraseña nueva"
            />
            <b-input-group-append is-text>
              <button
                type="button"
                class="auth-input__toggle"
                :aria-label="password1FieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                @click="togglePassword1Visibility"
              >
                <feather-icon :icon="password1ToggleIcon" />
              </button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Confirmación de contraseña"
          rules="required|confirmed:Password"
        >
          <label
            class="sr-only"
            for="reset-password-confirm"
          >Confirma tu contraseña</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="LockIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="reset-password-confirm"
              v-model="cPassword"
              :type="password2FieldType"
              autocomplete="new-password"
              placeholder="Confirma tu contraseña"
            />
            <b-input-group-append is-text>
              <button
                type="button"
                class="auth-input__toggle"
                :aria-label="password2FieldType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'"
                @click="togglePassword2Visibility"
              >
                <feather-icon :icon="password2ToggleIcon" />
              </button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <b-button
          type="submit"
          class="auth-submit"
          block
          :disabled="invalid || isSubmitting"
        >
          <b-spinner
            v-if="isSubmitting"
            small
          />
          <template v-else>
            <span>{{ isInvitation ? 'Crear contraseña' : 'Guardar contraseña' }}</span>
            <feather-icon
              icon="ArrowRightIcon"
              size="18"
            />
          </template>
        </b-button>
      </b-form>
    </validation-observer>

    <p class="auth-form__back">
      <b-link :to="{ name: 'auth-login' }">
        <feather-icon icon="ChevronLeftIcon" />
        Volver a iniciar sesión
      </b-link>
    </p>
  </auth-shell>
</template>

<script>
import { mapActions } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BForm,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BLink,
  BFormInput,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { required, min } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { startSession, landingRoute } from '@/auth/session'
import AuthShell from '@/components/auth/AuthShell.vue'

export default {
  components: {
    AuthShell,
    BButton,
    BForm,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BLink,
    BFormInput,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      cPassword: '',
      password: '',
      isSubmitting: false,
      required,
      min,
      password1FieldType: 'password',
      password2FieldType: 'password',
    }
  },
  computed: {
    isInvitation() {
      return this.$route.query.nueva === '1'
    },
    password1ToggleIcon() {
      return this.password1FieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    password2ToggleIcon() {
      return this.password2FieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    ...mapActions('auth', ['resetPassword']),
    togglePassword1Visibility() {
      this.password1FieldType = this.password1FieldType === 'password' ? 'text' : 'password'
    },
    togglePassword2Visibility() {
      this.password2FieldType = this.password2FieldType === 'password' ? 'text' : 'password'
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (!success || this.isSubmitting) return

        this.isSubmitting = true
        this.resetPassword({
          token: this.$route.params.token,
          user: {
            password: this.password,
            password_confirmation: this.cPassword,
          },
        })
          .then(response => {
            if (response?.token) {
              const userData = startSession(response)
              this.$router.replace(landingRoute(userData))
              return
            }
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Contraseña guardada',
                text: 'Ya puedes iniciar sesión.',
                icon: 'CheckCircleIcon',
                variant: 'success',
              },
            })
            this.$router.push({ name: 'auth-login' })
          })
          .catch(error => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'No pudimos guardar tu contraseña',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error?.messages?.[0],
              },
            })
          })
          .finally(() => {
            this.isSubmitting = false
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../components/auth/auth-page.scss';
</style>
